import api from '../api';

// Org-facing subscription / entitlements API (non-super-admin).
// The backend resolver returns the effective plan, lifecycle status, and live
// usage per limit — the single source of truth the Plan & Usage page renders.
export const SubscriptionAPI = {
  // GET /api/subscription/entitlements/
  // -> { plan, status, trial_ends_at, enforced,
  //      limits: { <key>: { limit, used } }, features, tiers, addons, billing }
  getEntitlements: async () => (await api.get('/api/subscription/entitlements/')).data,

  // Plan catalogue (marketing) — names/prices for all tiers.
  listPlans: async () => (await api.get('/api/subscription/')).data,

  // Org tax/billing profile (GST/VAT/EIN by country).
  getBillingProfile: async () => (await api.get('/api/subscription/billing-profile/')).data,
  saveBillingProfile: async (body) => (await api.put('/api/subscription/billing-profile/', body)).data,

  // ── Razorpay billing (org admins only) ────────────────────────────────────
  // Create a gateway subscription -> { subscription_id, key_id, ... } for the
  // Razorpay Checkout modal. Nothing activates until verify/webhook.
  createCheckout: async (planSlug, cycle) =>
    (await api.post('/api/subscription/checkout/', { plan_slug: planSlug, cycle })).data,
  // Checkout success callback -> activates the org on a valid signature.
  verifyCheckout: async (body) => (await api.post('/api/subscription/checkout/verify/', body)).data,
  // The BACKEND decides how a change happens, so the UI can't disagree with the
  // money. Response is either:
  //   mode: 'payment_required' + { order_id, amount, ... } — an upgrade: pay the
  //     one-off order for the prorated difference, then call verifyUpgrade.
  //   mode: 'scheduled' — downgrade / cycle switch, applied at cycle end.
  //   mode: 'reauthorize_required' + { subscription_id, ... } — an Indian-card
  //     mandate, which the gateway won't re-price. Approve the replacement
  //     mandate, POST the callback to verifyReauthorize, and it hands back a
  //     'payment_required' quote to finish through verifyUpgrade as usual.
  changePlan: async (planSlug, cycle) =>
    (await api.post('/api/subscription/change-plan/', { plan_slug: planSlug, cycle })).data,
  // Confirms an approved replacement mandate and returns the one-off order for the
  // prorated difference. Signature here is payment_id|subscription_id.
  verifyReauthorize: async (body) =>
    (await api.post('/api/subscription/upgrade/reauthorize/verify/', body)).data,
  // Completes a paid upgrade/add-on order: grants capacity now, moves the
  // recurring price from next cycle.
  verifyUpgrade: async (body) => (await api.post('/api/subscription/upgrade/verify/', body)).data,
  // Undo a scheduled (cycle-end) change before it lands.
  cancelScheduledChange: async () =>
    (await api.post('/api/subscription/change-plan/cancel-scheduled/')).data,
  // Price an add-on basket without creating anything — the capacity dialog calls
  // this on every quantity change. `items` is {users: 5, clusters: 2}.
  quoteAddons: async (items) =>
    (await api.post('/api/subscription/addons/quote/', { items })).data,
  // Buy a basket of add-ons in ONE payment. Returns mode 'payment_required'
  // (or 'reauthorize_required' on an Indian-card mandate) — same verify flow.
  buyAddons: async (items) =>
    (await api.post('/api/subscription/addons/', { items })).data,
  // Single-resource shorthand, still used by the 402 quota payload.
  buyAddon: async (resource, qty) =>
    (await api.post('/api/subscription/addons/', { resource, qty })).data,
  removeAddon: async (resource, qty) =>
    (await api.post('/api/subscription/addons/remove/', { resource, qty })).data,
  // Cancel at cycle end — access continues until the paid period ends.
  cancelSubscription: async () => (await api.post('/api/subscription/cancel/')).data,
  // Gateway-generated invoice history.
  listInvoices: async () => (await api.get('/api/subscription/invoices/')).data,
};

export default SubscriptionAPI;
